import { Alert, Stack, Text } from '@mantine/core';
import { IconPhone } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IntakeAction, IntakeAnswers } from '../types';
import { AppointmentDateTimePicker } from '../../../../components/AppointmentDateTimePicker';
import { bookableDateKeysInWindow } from '../intakeDataService';
import { getIntakeBookingWindow } from '../bookingConstraints';
import { formatUsTimeZoneLabel } from '../../../../lib/usTimeZoneLabel';
import {
  getHighRiskAvailability,
  type HighRiskAvailabilitySlot,
} from '../highRiskIntakeScheduling';
import { DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE } from '../../../../features/calendar/providerAvailabilitySchedule';

interface StepHighRiskScreeningBookingProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
}

/** ISO wall-time "HH:mm:ss" used by the TimeGrid (slice from the offset-local ISO string). */
function isoToTimeKey(iso: string): string {
  return iso.slice(11, 19);
}

export function StepHighRiskScreeningBooking({
  answers,
  dispatch,
}: StepHighRiskScreeningBookingProps): JSX.Element {
  const medplum = useMedplum();
  const { minDate, maxDate } = useMemo(() => getIntakeBookingWindow(), []);

  const [slots, setSlots] = useState<HighRiskAvailabilitySlot[]>([]);
  const [loadedDateKey, setLoadedDateKey] = useState<string | null>(null);
  const [availableWeekdays, setAvailableWeekdays] = useState<string[]>([]);
  const [timeZone, setTimeZone] = useState(DEFAULT_PROVIDER_AVAILABILITY_TIMEZONE);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const loadSeqRef = useRef(0);

  const availableDates = useMemo(() => {
    if (availableWeekdays.length === 0) return undefined;
    return bookableDateKeysInWindow(minDate, maxDate, availableWeekdays);
  }, [availableWeekdays, minDate, maxDate]);

  const loadSlotsForDate = useCallback(
    async (dateKey: string): Promise<void> => {
      const loadSeq = ++loadSeqRef.current;
      setSlotsLoading(true);
      setAvailabilityError(null);
      setSlots([]);
      try {
        const result = await getHighRiskAvailability(medplum, { date: dateKey });
        if (loadSeq !== loadSeqRef.current) return;
        setSlots(result.slots);
        setLoadedDateKey(dateKey);
        setAvailableWeekdays(result.availableWeekdays);
        setTimeZone(result.timeZone);
        // If a restored bookingTime no longer matches any loaded slot, clear it (and the resolved
        // slot) so a stale/conflicted time can't silently satisfy step validation.
        const currentTime = answers.bookingTime;
        if (currentTime) {
          const normalized = currentTime.length === 5 ? `${currentTime}:00` : currentTime;
          const stillValid = result.slots.some((s) => isoToTimeKey(s.start) === normalized);
          if (!stillValid) {
            dispatch({ type: 'SET_BOOKING_TIME', payload: null });
            dispatch({
              type: 'SET_SCREENING_SLOT',
              payload: { start: null, end: null, practitionerId: null },
            });
          }
        }
      } catch {
        if (loadSeq !== loadSeqRef.current) return;
        setSlots([]);
        setLoadedDateKey(null);
        setAvailabilityError('Could not load availability for this date.');
      } finally {
        if (loadSeq === loadSeqRef.current) setSlotsLoading(false);
      }
    },
    [medplum, dispatch, answers.bookingTime],
  );

  // Reload availability when returning to this step with a previously chosen date.
  useEffect(() => {
    if (answers.bookingDate && answers.bookingDate !== loadedDateKey) {
      void loadSlotsForDate(answers.bookingDate);
    }
    // Only on mount — date changes go through handleDateChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const liveTimeSlotsForDate = useCallback(
    (dateKey: string): string[] => {
      if (dateKey !== loadedDateKey) return [];
      return slots.map((s) => isoToTimeKey(s.start));
    },
    [slots, loadedDateKey],
  );

  const handleDateChange = useCallback(
    (dateKey: string) => {
      if (dateKey === loadedDateKey) return;
      dispatch({ type: 'SET_BOOKING_DATE', payload: dateKey });
      dispatch({ type: 'SET_BOOKING_TIME', payload: null });
      dispatch({ type: 'SET_SCREENING_SLOT', payload: { start: null, end: null, practitionerId: null } });
      void loadSlotsForDate(dateKey);
    },
    [dispatch, loadSlotsForDate, loadedDateKey],
  );

  const handleTimeChange = useCallback(
    (time: string | null) => {
      dispatch({ type: 'SET_BOOKING_TIME', payload: time });
      if (!time) {
        dispatch({ type: 'SET_SCREENING_SLOT', payload: { start: null, end: null, practitionerId: null } });
        return;
      }
      const normalized = time.length === 5 ? `${time}:00` : time;
      const slot = slots.find((s) => isoToTimeKey(s.start) === normalized);
      dispatch({
        type: 'SET_SCREENING_SLOT',
        payload: slot
          ? { start: slot.start, end: slot.end, practitionerId: slot.practitionerId }
          : { start: null, end: null, practitionerId: null },
      });
    },
    [dispatch, slots],
  );

  return (
    <Stack gap="md">
      <Alert variant="light" color="blue" icon={<IconPhone size={18} />}>
        <Text size="sm" fw={500}>
          We&rsquo;d like to schedule a brief call with a member of our intake team to learn more
          about your needs and make sure we find the best fit for you.
        </Text>
        <Text size="sm" c="dimmed" mt={4}>
          This is a 15-minute telehealth call, regardless of how you chose to meet for therapy.
        </Text>
      </Alert>

      <Text size="sm" c="dimmed">
        Choose a day and time for your intake call.
      </Text>
      {availabilityError ? (
        <Text size="sm" c="red">
          {availabilityError}
        </Text>
      ) : null}
      {!slotsLoading && loadedDateKey && slots.length === 0 ? (
        <Text size="sm" c="dimmed">
          No open times on this day. Try another date.
        </Text>
      ) : null}
      <AppointmentDateTimePicker
        selectedDate={answers.bookingDate}
        selectedTime={answers.bookingTime}
        onDateChange={handleDateChange}
        onTimeChange={handleTimeChange}
        minDate={minDate}
        maxDate={maxDate}
        liveTimeSlots={liveTimeSlotsForDate}
        availableDates={availableDates}
        timeZoneLabel={formatUsTimeZoneLabel(timeZone)}
        timesLoading={slotsLoading}
      />
    </Stack>
  );
}
